import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

import { AuthQuery } from '@app/auth/auth.query';
import { AuthDataService } from '@app/auth/auth-data.service';
import { SettingsService, RolePermissionMatrix } from '@app/services/settings.service';
import { ROLE_SUPERUSER, buildDefaultRolePermissionMatrix } from '@app/@shared/roles';

@Component({
  selector: 'app-superuser',
  templateUrl: './superuser.component.html',
  styleUrls: ['./superuser.component.css'],
})
export class SuperuserComponent implements OnInit {
  isNaN = isNaN;

  savingDemoMode = false;
  demoMode = false;

  aktenzeichenOverview: any[] = [];
  loadingOverview = false;

  rawSettingsJson = '';
  rawSettingsLoading = false;
  rawSettingsError = '';
  rawSettingsSaving = false;
  showRawSettingsConfirm = false;

  rolePermData: RolePermissionMatrix | null = null;
  rolePermMatrix: { [roleId: number]: { [module: string]: boolean } } = {};
  loadingRolePerms = false;
  savingRolePerms = false;
  rolePermError = '';
  readonly ROLE_SUPERUSER = ROLE_SUPERUSER;

  showSetDialog = false;
  savingAktenzeichen = false;
  newAktenzeichenValue = '';
  setDialogData: {
    dienstnr_id: string;
    name: string;
    bom: number;
    bomLabel: string;
    currentValue: number;
  } = { dienstnr_id: '', name: '', bom: 0, bomLabel: '', currentValue: 0 };

  constructor(
    private authQuery: AuthQuery,
    private authService: AuthDataService,
    private settingsService: SettingsService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadDemoMode();
    this.loadAktenzeichenOverview();
    this.loadRawSettings();
    this.applyRolePermData(buildDefaultRolePermissionMatrix());
    this.loadRolePermissions();
  }

  loadDemoMode(): void {
    this.settingsService.getDemoMode().subscribe(
      (response) => {
        this.demoMode = Number(response?.demo_mode ?? 0) === 1;
      },
      () => {
        this.demoMode = this.authQuery.demoModeActive;
      }
    );
  }

  loadAktenzeichenOverview(): void {
    this.loadingOverview = true;
    this.settingsService.getNextAktenzeichenOverview().subscribe(
      (response: any) => {
        this.aktenzeichenOverview = response.data || [];
        this.loadingOverview = false;
      },
      () => {
        this.loadingOverview = false;
      }
    );
  }

  loadRawSettings(): void {
    this.rawSettingsLoading = true;
    this.rawSettingsError = '';
    this.settingsService.getSettings().subscribe(
      (response) => {
        this.rawSettingsJson = JSON.stringify(response, null, 2);
        this.rawSettingsLoading = false;
      },
      () => {
        this.rawSettingsError = 'Fehler beim Laden der Einstellungen.';
        this.rawSettingsLoading = false;
      }
    );
  }

  saveRawSettings(): void {
    this.rawSettingsError = '';
    try {
      JSON.parse(this.rawSettingsJson);
    } catch (e: any) {
      this.rawSettingsError = 'Ungültiges JSON: ' + e.message;
      return;
    }
    this.showRawSettingsConfirm = true;
  }

  confirmSaveRawSettings(): void {
    this.showRawSettingsConfirm = false;
    this.rawSettingsError = '';

    let parsed: any;
    try {
      parsed = JSON.parse(this.rawSettingsJson);
    } catch (e: any) {
      this.rawSettingsError = 'Ungültiges JSON: ' + e.message;
      return;
    }

    this.rawSettingsSaving = true;
    const data = {
      username: this.authQuery.name,
      uid: this.authQuery.userid,
      token: this.authQuery.access_token,
      data: parsed,
    };

    this.settingsService
      .syncSettings(data)
      .pipe(finalize(() => (this.rawSettingsSaving = false)))
      .subscribe(
        () => {
          this.snackBar.open('DB Einstellungen gespeichert', 'Schließen', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 3000,
            panelClass: 'awesome-snackbar',
          });
          this.loadRawSettings();
        },
        (error) => {
          this.rawSettingsError = 'Fehler beim Speichern: ' + error;
          this.snackBar.open('Fehler beim Speichern', 'Schließen', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 3000,
          });
        }
      );
  }

  formatRawJson(): void {
    try {
      const parsed = JSON.parse(this.rawSettingsJson);
      this.rawSettingsJson = JSON.stringify(parsed, null, 2);
      this.rawSettingsError = '';
    } catch (e: any) {
      this.rawSettingsError = 'Ungültiges JSON: ' + e.message;
    }
  }

  openSetDialog(row: any, bom: number): void {
    this.setDialogData = {
      dienstnr_id: row.dienstnr_id,
      name: row.name,
      bom,
      bomLabel: bom === 0 ? 'Anzeige' : 'BOM',
      currentValue: bom === 0 ? row.next_anzeige : row.next_bom,
    };
    this.newAktenzeichenValue = '';
    this.showSetDialog = true;
  }

  closeSetDialog(): void {
    this.showSetDialog = false;
    this.newAktenzeichenValue = '';
  }

  confirmSetAktenzeichen(): void {
    if (!this.newAktenzeichenValue || isNaN(+this.newAktenzeichenValue)) {
      this.snackBar.open('Bitte eine gültige Zahl eingeben.', 'Schließen', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        duration: 3000,
      });
      return;
    }

    this.savingAktenzeichen = true;
    this.settingsService
      .setNextAktenzeichen(
        this.setDialogData.dienstnr_id,
        this.setDialogData.bom,
        this.newAktenzeichenValue,
        this.setDialogData.name
      )
      .subscribe(
        (response: any) => {
          this.savingAktenzeichen = false;
          if (response.result === 1) {
            this.snackBar.open(`Aktenzeichen gesetzt. Nächstes AKZ wird: ${response.next}`, 'Schließen', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 5000,
              panelClass: 'awesome-snackbar',
            });
            this.closeSetDialog();
            this.loadAktenzeichenOverview();
          } else {
            this.snackBar.open('Fehler: ' + (response.error || 'Unbekannter Fehler'), 'Schließen', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 5000,
            });
          }
        },
        () => {
          this.savingAktenzeichen = false;
          this.snackBar.open('Fehler beim Setzen des Aktenzeichens.', 'Schließen', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 3000,
          });
        }
      );
  }

  loadRolePermissions(): void {
    this.loadingRolePerms = true;
    this.rolePermError = '';
    this.settingsService.getRolePermissions().subscribe(
      (data: RolePermissionMatrix) => {
        if (!data?.roles?.length || !data?.modules?.length) {
          this.applyRolePermData(buildDefaultRolePermissionMatrix(), 'Leere API-Antwort – Standard-Matrix geladen.');
          return;
        }
        this.applyRolePermData(data);
      },
      () => {
        this.applyRolePermData(
          buildDefaultRolePermissionMatrix(),
          'Rollen & Rechte konnten nicht vom Server geladen werden. Standard-Matrix angezeigt – Speichern schlägt fehl, bis die API erreichbar ist.'
        );
      }
    );
  }

  private applyRolePermData(data: RolePermissionMatrix, warning?: string): void {
    this.rolePermData = data;
    this.rolePermMatrix = this.normalizeRolePermMatrix(data.matrix || {});
    this.loadingRolePerms = false;
    this.rolePermError = warning || '';
  }

  private normalizeRolePermMatrix(matrix: { [key: string]: { [module: string]: boolean } }): {
    [roleId: number]: { [module: string]: boolean };
  } {
    const normalized: { [roleId: number]: { [module: string]: boolean } } = {};
    Object.keys(matrix || {}).forEach((roleKey) => {
      normalized[+roleKey] = { ...(matrix[roleKey] || {}) };
    });
    return normalized;
  }

  isRolePermAllowed(roleId: number, module: string): boolean {
    return !!this.rolePermMatrix?.[roleId]?.[module];
  }

  setRolePermAllowed(roleId: number, module: string, allowed: boolean): void {
    if (!this.rolePermMatrix[roleId]) {
      this.rolePermMatrix[roleId] = {};
    }
    this.rolePermMatrix[roleId][module] = allowed;
  }

  saveRolePermissions(): void {
    this.savingRolePerms = true;
    this.settingsService.saveRolePermissions(this.rolePermMatrix).subscribe(
      () => {
        this.savingRolePerms = false;
        this.snackBar.open('Rollen & Rechte gespeichert', 'Schließen', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          duration: 3000,
          panelClass: 'awesome-snackbar',
        });
        this.loadRolePermissions();
      },
      () => {
        this.savingRolePerms = false;
        this.snackBar.open('Fehler beim Speichern der Rollen & Rechte', 'Schließen', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          duration: 3000,
        });
      }
    );
  }

  toggleDemoMode(enabled: boolean): void {
    const demoMode = enabled ? 1 : 0;
    this.savingDemoMode = true;
    this.settingsService.setDemoMode(demoMode).subscribe(
      (response) => {
        this.savingDemoMode = false;
        if (response?.result === 1) {
          this.demoMode = Number(response.demo_mode) === 1;
          this.authService.updateDemoMode(response.demo_mode);
          this.snackBar.open(
            this.demoMode
              ? 'Demo-Mode aktiviert – Bearbeitung und Massenaktionen gesperrt'
              : 'Demo-Mode deaktiviert – normaler Betrieb',
            'Schließen',
            {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 4000,
              panelClass: 'awesome-snackbar',
            }
          );
        }
      },
      () => {
        this.savingDemoMode = false;
        this.snackBar.open('Fehler beim Umschalten des Demo-Mode', 'Schließen', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          duration: 3000,
        });
      }
    );
  }
}
